import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import TierBadge from "@/components/TierBadge";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { TIERS } from "@/lib/tiers";
import { CheckCircle, X, Wallet, Gavel, ShieldCheck, IdentificationCard } from "@phosphor-icons/react";

const AdminPage = () => {
  const [tierRequests, setTierRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingReleases, setPendingReleases] = useState([]);
  const [idVerifications, setIdVerifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [tr, pa, pr, iv] = await Promise.all([
        api.get("/admin/tier-requests"),
        api.get("/admin/jobs?status=awaiting_admin_approval"),
        api.get("/admin/jobs?status=awaiting_release"),
        api.get("/admin/id-verifications"),
      ]);
      setTierRequests(tr.data);
      setPendingApprovals(pa.data);
      setPendingReleases(pr.data);
      setIdVerifications(iv.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decideTier = async (userId, decision) => {
    try {
      await api.post(`/admin/tier-requests/${userId}`, { decision });
      toast.success(decision === "approve" ? "Tier approved" : "Tier rejected");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const approveJob = async (jobId) => {
    try {
      await api.post(`/admin/jobs/${jobId}/approve`);
      toast.success("Job approved — awaiting client payment");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const rejectJob = async (jobId) => {
    try {
      await api.post(`/admin/jobs/${jobId}/reject`);
      toast.success("Job rejected — reset to open");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const releaseEscrow = async (jobId) => {
    try {
      await api.post(`/admin/jobs/${jobId}/release-escrow`);
      toast.success("Escrow released — contractor credited");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const decideId = async (userId, decision) => {
    try {
      await api.post(`/admin/id-verifications/${userId}`, { decision });
      toast.success(decision === "verify" ? "ID verified" : "ID rejected");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="admin-page">
      <Toaster position="top-right" richColors />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16">
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
            Admin console
          </p>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
            Approvals & escrow
          </h1>
          <p className="mt-3 text-base leading-relaxed text-[#5C605E]">
            Approve accepted jobs, release escrow when work is done, and grade contractors up the reliability ladder.
          </p>
        </div>

        <Tabs defaultValue="approvals" className="mt-10">
          <TabsList className="grid w-full grid-cols-4 rounded-md border border-[#E5E2DB] bg-white p-1">
            <TabsTrigger
              value="approvals"
              data-testid="admin-tab-approvals"
              className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
            >
              Job approvals ({pendingApprovals.length})
            </TabsTrigger>
            <TabsTrigger
              value="releases"
              data-testid="admin-tab-releases"
              className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
            >
              Escrow releases ({pendingReleases.length})
            </TabsTrigger>
            <TabsTrigger
              value="tiers"
              data-testid="admin-tab-tiers"
              className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
            >
              Tier requests ({tierRequests.length})
            </TabsTrigger>
            <TabsTrigger
              value="ids"
              data-testid="admin-tab-ids"
              className="rounded-sm data-[state=active]:bg-[#0A0F0D] data-[state=active]:text-[#FFFFFF]"
            >
              ID verifications ({idVerifications.length})
            </TabsTrigger>
          </TabsList>

          {/* JOB APPROVALS */}
          <TabsContent value="approvals" className="mt-6 space-y-4" data-testid="admin-approvals-list">
            {loading ? (
              <p className="text-[#5C605E]">Loading…</p>
            ) : pendingApprovals.length === 0 ? (
              <EmptyState icon={Gavel} title="No jobs waiting for approval" />
            ) : (
              pendingApprovals.map((j) => {
                const cat = CATEGORIES.find((c) => c.id === j.category);
                return (
                  <div
                    key={j.id}
                    className="rounded-md border border-[#E5E2DB] bg-white p-5"
                    data-testid={`admin-approval-${j.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <span className="rounded-sm bg-[#0A0F0D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#FFFFFF]">
                          {cat?.label || j.category}
                        </span>
                        <h3 className="mt-2 font-display text-lg font-bold text-[#0A0F0D]">
                          {j.title}
                        </h3>
                        <p className="mt-1 text-sm text-[#5C605E]">
                          Client: {j.client_name} · Accepted price:{" "}
                          <strong className="text-[#0A0F0D]">
                            R{Number(j.accepted_price || 0).toFixed(2)}
                          </strong>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/jobs/${j.id}`}>
                          <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          onClick={() => rejectJob(j.id)}
                          data-testid={`admin-reject-job-${j.id}`}
                          variant="outline"
                          className="border-[#5C605E] text-[#5C605E]"
                        >
                          <X size={14} weight="bold" className="mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approveJob(j.id)}
                          data-testid={`admin-approve-job-${j.id}`}
                          className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                        >
                          <CheckCircle size={14} weight="bold" className="mr-1" /> Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* ESCROW RELEASES */}
          <TabsContent value="releases" className="mt-6 space-y-4" data-testid="admin-releases-list">
            {loading ? (
              <p className="text-[#5C605E]">Loading…</p>
            ) : pendingReleases.length === 0 ? (
              <EmptyState icon={Wallet} title="No escrow releases pending" />
            ) : (
              pendingReleases.map((j) => (
                <div
                  key={j.id}
                  className="rounded-md border border-[#E5E2DB] bg-white p-5"
                  data-testid={`admin-release-${j.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-lg font-bold text-[#0A0F0D]">{j.title}</h3>
                      <p className="mt-1 text-sm text-[#5C605E]">
                        Escrow amount:{" "}
                        <strong className="text-[#0A0F0D]">
                          R{Number(j.escrow_amount || 0).toFixed(2)}
                        </strong>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/jobs/${j.id}`}>
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        onClick={() => releaseEscrow(j.id)}
                        data-testid={`admin-release-escrow-${j.id}`}
                        className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                      >
                        <Wallet size={14} weight="bold" className="mr-1" /> Release escrow
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* TIER REQUESTS */}
          <TabsContent value="tiers" className="mt-6 space-y-4" data-testid="admin-tier-requests-list">
            {loading ? (
              <p className="text-[#5C605E]">Loading…</p>
            ) : tierRequests.length === 0 ? (
              <EmptyState icon={ShieldCheck} title="No tier requests pending" />
            ) : (
              tierRequests.map((u) => {
                const req = u.contractor_profile?.pending_tier_request;
                const current = u.contractor_profile?.tier || "none";
                const completed = u.contractor_profile?.completed_jobs || 0;
                return (
                  <div
                    key={u.id}
                    className="rounded-md border border-[#E5E2DB] bg-white p-5"
                    data-testid={`admin-tier-request-${u.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-bold text-[#0A0F0D]">
                          {u.contractor_profile?.business_name || u.name}
                        </p>
                        <p className="mt-1 text-sm text-[#5C605E]">
                          {u.email} · {completed} completed jobs · category: {u.contractor_profile?.category}
                        </p>
                        <div className="mt-3 flex items-center gap-2 text-xs">
                          <span className="text-[#5C605E]">Currently:</span>
                          <TierBadge tier={current} size="sm" />
                          <span className="text-[#5C605E]">→</span>
                          <TierBadge tier={req} size="sm" />
                          <span className="ml-2 text-[#5C605E]">
                            (needs {TIERS[req]?.threshold || 0})
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => decideTier(u.id, "reject")}
                          data-testid={`admin-tier-reject-${u.id}`}
                          className="border-[#5C605E] text-[#5C605E]"
                        >
                          <X size={14} weight="bold" className="mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => decideTier(u.id, "approve")}
                          data-testid={`admin-tier-approve-${u.id}`}
                          className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                        >
                          <CheckCircle size={14} weight="bold" className="mr-1" /> Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* ID VERIFICATIONS */}
          <TabsContent value="ids" className="mt-6 space-y-4" data-testid="admin-id-verifications-list">
            {loading ? (
              <p className="text-[#5C605E]">Loading…</p>
            ) : idVerifications.length === 0 ? (
              <EmptyState icon={IdentificationCard} title="No ID verifications pending" />
            ) : (
              idVerifications.map((u) => {
                const p = u.contractor_profile || {};
                const token = getAuthToken();
                const idPhotoUrl = p.id_photo_id
                  ? `${API}/uploads/${p.id_photo_id}?auth=${token}`
                  : null;
                return (
                  <div
                    key={u.id}
                    className="flex flex-wrap items-start gap-4 rounded-md border border-[#E5E2DB] bg-white p-5"
                    data-testid={`admin-id-verification-${u.id}`}
                  >
                    {idPhotoUrl && (
                      <img
                        src={idPhotoUrl}
                        alt="ID document"
                        className="h-32 w-48 flex-shrink-0 rounded-md border border-[#E5E2DB] object-cover"
                        data-testid={`admin-id-photo-${u.id}`}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-display text-lg font-bold text-[#0A0F0D]">
                        {p.business_name || u.name}
                      </p>
                      <p className="mt-1 text-sm text-[#5C605E]">
                        {u.email} · category: {p.category} · submitted{" "}
                        {p.id_submitted_at ? new Date(p.id_submitted_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decideId(u.id, "reject")}
                        data-testid={`admin-id-reject-${u.id}`}
                        className="border-[#5C605E] text-[#5C605E]"
                      >
                        <X size={14} weight="bold" className="mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => decideId(u.id, "verify")}
                        data-testid={`admin-id-verify-${u.id}`}
                        className="bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
                      >
                        <CheckCircle size={14} weight="bold" className="mr-1" /> Verify
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const EmptyState = ({ icon: Icon, title }) => (
  <div className="rounded-md border border-dashed border-[#D6D3CD] bg-white p-12 text-center">
    <Icon size={40} weight="duotone" color="#5C605E" className="mx-auto" />
    <p className="mt-3 font-display text-lg font-semibold text-[#0A0F0D]">{title}</p>
  </div>
);

export default AdminPage;
